/**
 * "Qual endereço eu escrevo num link que vai SAIR do app?" — FONTE ÚNICA.
 *
 * ── Por que isto precisa existir (BUG-116, 13/08/2026) ──────────────────────
 * Até hoje o app nativo servia o bundle de `https://uselivra.com` (Android) e
 * `capacitor://uselivra.com` (iOS), porque `server.hostname` era o domínio
 * real. No Android isso fazia `window.location.origin` devolver
 * `https://uselivra.com` — o endereço CERTO, **por acidente**: o valor não
 * vinha de o app saber onde o site mora, vinha de o servidor local ter roubado
 * o nome do site.
 *
 * Trocar o hostname para `localhost` derruba esse acidente. Sem este módulo,
 * todo link que a pessoa COPIA OU COMPARTILHA passaria a sair como
 * `https://localhost/...` (Android) e `capacitor://localhost/...` (iOS) — que
 * é exatamente o que o creator manda para os seguidores dele e o que a pessoa
 * manda para as amigas ao compartilhar o perfil. No iOS esses links já estavam
 * quebrados antes da troca; o que a troca faz é levar o Android junto, e é por
 * isso que o conserto anda no MESMO commit que a mudança de origem.
 *
 * ── A regra ────────────────────────────────────────────────────────────────
 * Na WEB o comportamento é **byte a byte o de antes**: `window.location.origin`.
 * É deliberado — assim um preview channel continua compartilhando o link do
 * próprio preview, e esta mudança não tem como regredir nada em produção web.
 * Só no build nativo a resposta passa a ser o domínio público, porque lá
 * `location` descreve o servidor embutido, não um endereço que exista para
 * outra pessoa.
 *
 * É a mesma classe do `resolveAuthDomain` (`lib/firebase/config.ts`): uma
 * heurística nascida quando o app só existia na web, que dentro do Capacitor
 * responde sobre o bundle local em vez de sobre a internet. Aquela caiu no
 * build 23; esta cai aqui.
 *
 * ⚠️ Não use isto para navegação INTERNA (rotas do React Router). Rota interna
 * é caminho relativo e continua sendo — absolutizar rota interna no nativo faz
 * o app sair para o navegador em vez de navegar.
 */

import { isNativeApp } from "./native";

/**
 * Onde o Livra mora para o resto do mundo.
 *
 * Espelha o `BASE` de `scripts/landing-seo-content.mjs`, que é o canônico do
 * build (pré-render, sitemap, JSON-LD). Os dois não podem ser importados um do
 * outro — um é módulo de build em `.mjs`, o outro é bundle do app —, então a
 * paridade é travada por teste em `tests/unit/nativeOriginGuard.test.ts`.
 */
export const PUBLIC_SITE_ORIGIN = "https://uselivra.com";

/**
 * Origem a usar em URL destinada a sair do app (compartilhar, copiar, canonical).
 *
 * Web → a origem atual (comportamento histórico preservado).
 * Nativo → o domínio público, porque a origem local não existe para ninguém.
 */
export function shareableOrigin(): string {
  if (isNativeApp()) return PUBLIC_SITE_ORIGIN;
  if (typeof window === "undefined") return PUBLIC_SITE_ORIGIN;
  return window.location.origin;
}

/**
 * Absolutiza um caminho do app para uso externo.
 *
 * URL já absoluta passa direto — os call sites de SEO recebem os dois formatos
 * e reescrever uma URL absoluta que alguém passou de propósito seria pior que
 * não fazer nada.
 */
export function publicUrl(path: string): string {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(path)) return path;
  const base = shareableOrigin();
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
}
